import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Calendar, Shield, Factory } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { companyInfo } from '../mock/data';

const Businesses = () => {
  const businessHighlights = [
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Halal Certified",
      description: "First Cuisine specializes in halal food manufacturing with full certification"
    },
    {
      icon: <Factory className="h-8 w-8 text-blue-500" />,
      title: "Non-Halal Expertise",
      description: "PS Catering provides comprehensive non-halal food services"
    },
    {
      icon: <Calendar className="h-8 w-8 text-purple-500" />,
      title: "Value-Added Services",
      description: "FC Food Industries offers bakery and frozen food production"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Businesses</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Three specialized companies united under Teng Food Holdings, 
              each bringing unique expertise and capabilities to serve diverse market needs.
            </p>
          </div>
        </div>
      </section>

      {/* Business Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {businessHighlights.map((highlight, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {highlight.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{highlight.title}</h3>
                  <p className="text-gray-600">{highlight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* First Cuisine - Halal Manufacturing */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="h-8 w-8 text-green-500" />
                <span className="text-green-600 font-semibold">Halal Manufacturing</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {companyInfo.subsidiaries.firstCuisine.name}
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>{companyInfo.subsidiaries.firstCuisine.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Est. {companyInfo.subsidiaries.firstCuisine.established}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">UEN: {companyInfo.subsidiaries.firstCuisine.uen}</span>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Services:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {companyInfo.subsidiaries.firstCuisine.services.map((service, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <ArrowRight className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-600">{service}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Therapeutic Diets */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Therapeutic Diets:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {companyInfo.subsidiaries.firstCuisine.diets.map((diet, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <ArrowRight className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-gray-600">{diet}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button className="bg-green-600 hover:bg-green-700">
                <Link to="/expertise/therapeutic-diets" className="flex items-center">
                  Learn More About Therapeutic Diets
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1652211955967-99c892925469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjAwMDM3NzN8MA&ixlib=rb-4.1.0&q=85"
                alt="Halal Food Manufacturing"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Premises */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Facilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {companyInfo.subsidiaries.firstCuisine.premises.map((premise, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-2">{premise.type}</h4>
                    <div className="flex items-start space-x-2 mb-3">
                      <MapPin className="h-4 w-4 text-blue-500 mt-0.5" />
                      <span className="text-sm text-gray-600">{premise.address}</span>
                    </div>
                    <span className="text-sm text-blue-600 font-medium">{premise.model}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PS Catering - Non-Halal */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1563468304224-1fc761a1cbb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Non-Halal Food Manufacturing"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Factory className="h-8 w-8 text-blue-500" />
                <span className="text-blue-600 font-semibold">Non-Halal Manufacturing</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {companyInfo.subsidiaries.psCatering.name}
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>{companyInfo.subsidiaries.psCatering.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Est. {companyInfo.subsidiaries.psCatering.established}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">UEN: {companyInfo.subsidiaries.psCatering.uen}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-blue-500 mt-0.5" />
                  <span className="text-sm text-gray-600">{companyInfo.subsidiaries.psCatering.address}</span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Services:</h4>
                {companyInfo.subsidiaries.psCatering.services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <ArrowRight className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600">{service}</span>
                  </div>
                ))}
              </div>

              <Button className="bg-blue-600 hover:bg-blue-700">
                <Link to="/contact" className="flex items-center">
                  Contact PS Catering
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FC Food Industries - Value-Added */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Calendar className="h-8 w-8 text-purple-500" />
                <span className="text-purple-600 font-semibold">Value-Added Production</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {companyInfo.subsidiaries.fcIndustries.name}
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>{companyInfo.subsidiaries.fcIndustries.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Est. {companyInfo.subsidiaries.fcIndustries.established}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">UEN: {companyInfo.subsidiaries.fcIndustries.uen}</span>
                  </div>
                </div>
              </div>

              <Button className="bg-purple-600 hover:bg-purple-700">
                <Link to="/oem-services" className="flex items-center">
                  Explore OEM Services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Bakery and Food Production"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* FC Industries Premises */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Production Facilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {companyInfo.subsidiaries.fcIndustries.premises.map((premise, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-2">{premise.type}</h4>
                    <div className="flex items-start space-x-2 mb-3">
                      <MapPin className="h-4 w-4 text-purple-500 mt-0.5" />
                      <span className="text-sm text-gray-600">{premise.address}</span>
                    </div>
                    <div className="mt-3">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Products:</h5>
                      <div className="grid grid-cols-2 gap-1">
                        {premise.products.map((product, pIndex) => (
                          <span key={pIndex} className="text-xs text-gray-600 capitalize">• {product}</span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Businesses;